import configAxios from "../configuration/configAxios";
import { errorHandlerServices } from "../configuration/errorHandle";
import { currentDateEcuador, formatDateHoursStringDoc } from "../../utils/daysOptions";
import axios, { AxiosResponse } from "axios";
import {getTextByParameter} from "../../utils/textOptions";
import {isValidNumber} from "../../utils/numberOptions";
import {attachCancelToken, buildRequestKey, cleanupCancelToken} from "../configuration/cancelServices";
import {interceptorResponse} from "../../utils/decode/decoding";

/**
 * Interfaz base para los servicios HTTP
 *
 * @param baseURL (string, opcional): URL base del servicio. Si no se especifica, se usará la configuración por defecto de `configAxios`.
 * @param service (string, obligatorio): Endpoint o ruta del servicio a consumir (por ejemplo: `"/api/v1/users"`).
 * @param data (any, opcional): Cuerpo del request (payload) enviado al servicio.
 * @param attachResponse (any, opcional): Objeto auxiliar que se fusionará con la respuesta recibida del backend.
 * @param returnAttachResponse (boolean, opcional): Si es `true`, devuelve el objeto `attachResponse` en lugar del cuerpo del servicio.
 * @param redirect (boolean, opcional): Controla si se debe redirigir al login. Por defecto `true`.
 * @param alertToast (boolean, opcional): Indica el estilo de mensajes emergentes (toast).
 * @param hiddenAlert (boolean, opcional): Si se deben mostrar mensajes visuales.
 * @param returnMessage (boolean, opcional): Si es `true`, retorna únicamente el mensaje de respuesta (`message`) del backend.
 * @param title (string, opcional): Título utilizado en las alertas o mensajes de error.
 * @param responseType ('blob', opcional): Tipo de respuesta esperada (por ejemplo, `blob` para descargas de archivos).
 * @param fileName (string, opcional): Nombre del archivo generado si es blob (por defecto `"doc"` + fecha/hora).
 * @param onError (() => void, opcional): Acción a ejecutar luego de visualizar un error.
 * @param cancelPrevious (boolean, opcional): Des habilita la cancelación de servicios repetidos, por defecto `true`.
 * @param cancelKey (string, opcional): forzar una key custom para cancelar servicios repetidos
 */
interface Props {
    baseURL?: string;
    service: string;
    data?: any;
    attachResponse?: any;
    returnAttachResponse?: boolean;
    redirect?: boolean;
    alertToast?: boolean;
    hiddenAlert?: boolean;
    returnMessage?: boolean;
    title?: string;
    responseType?: "blob";
    fileName?: string;
    onError?: () => void;
    cancelPrevious?: boolean,
    cancelKey?: string
}

interface ResponseProps extends Props {
    response: AxiosResponse;
    service: string;
}

interface ErrorProps extends Props {
    error: any;
}

/**
 * Función `setResponse`:
 *
 * Procesa y normaliza la respuesta obtenida desde Axios, devolviendo
 * el formato de datos correcto según el tipo de respuesta (`JSON` o `blob`).
 *
 * **Comportamiento:**
 * - Si `responseType` es `'blob'`, retorna un objeto `{ blob, fileName }`.
 * - Si `returnMessage` es `true`, retorna `response.data.message`.
 * - Si `returnAttachResponse` es `true`, retorna el objeto `attachResponse`.
 * - Si `response.data.object` es un array, lo combina con `attachResponse` en cada elemento.
 * - Si es un objeto o string, lo retorna directamente.
 *
 * **Validaciones:**
 * - Si el `status` del backend es distinto de `1`, lanza un error con el mensaje recibido.
 *
 * @param response (AxiosResponse): Respuesta HTTP original.
 * @param responseType
 * @param fileName
 * @param returnMessage
 * @param returnAttachResponse
 * @param attachResponse
 * @returns (any): Datos procesados del backend según el tipo de respuesta.
 * @throws {Error} Si el servicio retorna un estado no exitoso (`status != 1`).
 */
const setResponse = ({
                         response,
                         responseType,
                         fileName,
                         returnMessage,
                         returnAttachResponse,
                         attachResponse,
                     }: ResponseProps): any => {
    const status = response?.data?.status;

    // error backend
    if ((isValidNumber(status) && status !== 1) || (responseType === "blob" && (!!status || status === 0))) {
        throw new Error(response?.data?.message);
    }

    // blob
    if (responseType === "blob") {
        return {
            blob: response.data,
            fileName: `${fileName || "doc"}-${formatDateHoursStringDoc(currentDateEcuador())}.pdf`,
        };
    }

    // solo mensaje
    if (returnMessage) return response.data.message;

    // attachResponse solo
    if (returnAttachResponse) return attachResponse;

    // listas u objetos
    const obj = interceptorResponse(response.data.object);

    if (Array.isArray(obj)) {
        return obj.map((item: any) => ({ ...attachResponse, ...item }));
    }
    if (typeof obj === "string" || typeof obj === "boolean" || typeof obj === "number") {
        return obj;
    }

    return { ...attachResponse, ...obj };
};

/**
 * Función `setError`:
 *
 * Centraliza el manejo de errores provenientes de los servicios HTTP.
 *
 * **Comportamiento:**
 * - Muestra mensajes visuales si `alertToast` es `true`.
 * - Propaga una excepción con el mensaje del backend.
 *
 * @param error (any): Objeto de error capturado (Axios o interno).
 * @param redirect (boolean, opcional): Si se debe redirigir en caso de error.
 * @param alertToast (boolean, opcional): Indica el estilo de mensajes emergentes (toast).
 * @param hiddenAlert (boolean, opcional): Si se deben mostrar mensajes visuales.
 * @throws {Error} Conteniendo el mensaje del backend o `"service <nombre>"`.
 */
const setError = ({error, redirect, service, hiddenAlert}: ErrorProps): never => {
    errorHandlerServices(
        error,
        redirect ?? true,
        service,
        undefined,
        undefined,
        hiddenAlert,
    );

    throw new Error(error?.response?.data?.message || `${getTextByParameter(process.env.REACT_APP_MSJ_ERROR_SERVICE)}`);
};

/**
 * Tipos internos usados por la función requestService
 */
type HttpMethod = "get" | "post";

interface RequestOptions extends Props {
    method: HttpMethod;
}

/**
 * Función genérica `requestService`:
 *
 * Centraliza la lógica repetida entre `postService` y `getService`.
 *
 * **Ventajas:**
 * - Evita duplicación de llamadas Axios.
 * - Unifica manejo de errores y respuestas.
 * - Permite agregar nuevos métodos HTTP fácilmente.
 *
 * **Flujo interno:**
 * 1. Ejecuta la llamada HTTP con Axios.
 * 2. Procesa la respuesta mediante `setResponse`.
 * 3. Maneja errores mediante `setError`.
 *
 * @param method ('get' | 'post'): Función HTTP a ejecutar.
 * @param baseURL
 * @param service
 * @param data
 * @param attachResponse
 * @param returnAttachResponse
 * @param redirect
 * @param alertToast
 * @param hiddenAlert
 * @param returnMessage
 * @param title
 * @param responseType
 * @param fileName
 * @param onError
 * @param cancelPrevious
 * @param cancelKey
 * @returns {Promise<any | void>}
 */
const requestService = async ({
                                  method,
                                  baseURL,
                                  service,
                                  data,
                                  attachResponse,
                                  returnAttachResponse,
                                  redirect,
                                  alertToast,
                                  hiddenAlert,
                                  returnMessage,
                                  title,
                                  responseType,
                                  fileName,
                                  onError,
                                  cancelPrevious,
                                  cancelKey
                              }: RequestOptions): Promise<any | void> => {
    // ✅ por defecto: cancelar si se repite
    const shouldCancelPrevious = cancelPrevious !== undefined ? cancelPrevious : true;

    // key del request repetido
    const key = cancelKey ?? buildRequestKey(method, baseURL, service, data);

    // config axios
    const axiosConfig: any = { responseType, baseURL };

    // ✅ attach cancel token si aplica
    if (shouldCancelPrevious) {
        axiosConfig.cancelToken = attachCancelToken(key);
    }


    try {
        // llamada axios
        let response;
        if (method === "post") {
            response = await configAxios.post(service, data, axiosConfig);
        } else {
            response = await configAxios.get(service, axiosConfig);
        }

        return setResponse({
            response,
            responseType,
            fileName,
            returnMessage,
            returnAttachResponse,
            attachResponse,
            service,
        });
    } catch (error: any) {
        // ✅ si fue cancelación, NO alertas, NO redirect, NO throw de negocio
        if (axios.isCancel(error)) return;

        setError({
            error,
            redirect,
            alertToast,
            hiddenAlert,
            title,
            service,
            onError,
        });
    } finally {
        if (shouldCancelPrevious) cleanupCancelToken(key);
    }
};

/**
 * Función `postService`:
 *
 * Realiza una solicitud HTTP **POST cifrada** al backend.
 *
 * @returns {Promise<any | void>} Respuesta procesada del backend.
 * @throws {Error} Si hay error de backend o de red.
 */
export const postService = (props: Props): Promise<any | void> => {
    return requestService({
        ...props,
        method: "post"
    });
};

/**
 * Función `getService`:
 *
 * Realiza una solicitud HTTP **GET** al backend usando Axios.
 *
 * @returns {Promise<any | void>} Respuesta procesada.
 * @throws {Error} Si el servicio falla.
 */
export const getService = (props: Props): Promise<any | void> => {
    return requestService({
        ...props,
        method: "get"
    });
};
